import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Star, RotateCcw, Loader2, ImageIcon, Upload, GripVertical, Eye, EyeOff } from "lucide-react";
import { api, API_BASE, TOKEN_KEY } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { InvoiceDesignPreview, TEMPLATE_PRESETS, SECTION_DEFAULTS, resolveDesign } from "@/components/InvoiceDesignPreview";

const FONTS = [{ v: "sans", l: "Sans (modern)" }, { v: "serif", l: "Serif (classic)" }, { v: "mono", l: "Monospace" }];
const HEADERS = [{ v: "standard", l: "Accent rule" }, { v: "band", l: "Colour band" }, { v: "minimal", l: "Minimal line" }];
const DENSITY = [{ v: "comfortable", l: "Comfortable" }, { v: "compact", l: "Compact" }];
const LAYOUT = [{ v: "unified", l: "One combined table" }, { v: "grouped", l: "Separate Labour & Parts" }];
const TOGGLES = [
  { k: "show_logo", l: "Business logo" },
  { k: "show_vehicle", l: "Vehicle details & registration" },
  { k: "show_payment_details", l: "Payment / bank details" },
  { k: "show_terms", l: "Terms & conditions" },
  { k: "show_signature", l: "Signature block" },
  { k: "show_thank_you", l: "Thank-you line" },
  { k: "show_contact", l: "Footer contact details" },
];
const ARRANGE = [
  { k: "payment_details", l: "Payment details", toggle: "show_payment_details" },
  { k: "thank_you", l: "Thank-you message", toggle: "show_thank_you" },
  { k: "terms", l: "Terms & conditions", toggle: "show_terms" },
  { k: "signature", l: "Signature block", toggle: "show_signature" },
];
const ALLOWED_ORDER = ARRANGE.map((a) => a.k);

function expand(tpl, overrides = {}) {
  const p = TEMPLATE_PRESETS[tpl] || TEMPLATE_PRESETS.modern;
  return {
    template: tpl,
    primary_color: p.accent,
    secondary_color: p.dark,
    font: p.font,
    header_style: p.header_style,
    density: p.density,
    labour_parts: p.labour_parts,
    ...SECTION_DEFAULTS,
    logo_size: 14,
    logo_crop: false,
    section_order: [...ALLOWED_ORDER],
    footer_text: "",
    ...overrides,
  };
}

export default function InvoiceDesign() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("template");
  const [design, setDesign] = useState(expand("modern"));
  const [savedTemplate, setSavedTemplate] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [garageName, setGarageName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api("/garage/settings").then((g) => {
    setGarageName(g?.name || "");
    setLogoUrl(g?.logo_data_url || null);
    const saved = g?.invoice_design;
    if (saved && saved.template) {
      setDesign(expand(saved.template, saved));
      setSavedTemplate(saved.template);
    }
  }).catch(() => {});

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await fetch(`${API_BASE}/garage/logo`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }, body: fd });
      await load();
    } finally {
      setUploading(false);
    }
  };

  const set = (patch) => setDesign((d) => ({ ...d, ...patch }));

  const [dragKey, setDragKey] = useState(null);
  const reorder = (from, to) => {
    setDesign((d) => {
      const arr = [...d.section_order];
      const fi = arr.indexOf(from);
      const ti = arr.indexOf(to);
      if (fi < 0 || ti < 0 || fi === ti) return d;
      arr.splice(ti, 0, arr.splice(fi, 1)[0]);
      return { ...d, section_order: arr };
    });
  };
  const move = (key, dir) => {
    setDesign((d) => {
      const arr = [...d.section_order];
      const i = arr.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, section_order: arr };
    });
  };

  const save = async (next) => {
    const payload = next || design;
    setSaving(true);
    setSavedMsg("");
    try {
      await api("/garage/settings", { method: "PATCH", body: JSON.stringify({ invoice_design: payload }) });
      setSavedTemplate(payload.template);
      setSavedMsg("Saved — new invoices will use this design.");
      setTimeout(() => setSavedMsg(""), 4000);
    } catch {
      setSavedMsg("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  }

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <button onClick={() => navigate("/business-settings")} data-testid="invoice-design-back" className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Business Settings</button>
      <PageHeader title="Invoice Design" subtitle="Choose a template and personalise how your invoices look" />

      {/* Tabs */}
      <div className="mb-6 inline-flex rounded-lg border border-border bg-secondary p-1" data-testid="invoice-design-tabs">
        {[["template", "Choose Template"], ["customise", "Customise Design"], ["arrange", "Arrange Sections"]].map(([v, l]) => (
          <button key={v} data-testid={`tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-md px-4 py-2 text-xs font-extrabold tracking-wide transition-colors ${tab === v ? "bg-midnight text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {savedMsg && <div data-testid="invoice-design-saved" className="mb-4 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm font-semibold text-success">{savedMsg}</div>}

      {tab === "template" && (
        <div className="grid gap-5 md:grid-cols-2" data-testid="template-gallery">
          {Object.entries(TEMPLATE_PRESETS).map(([id, p]) => {
            const isSelected = design.template === id;
            const isDefault = savedTemplate === id;
            return (
              <div key={id} data-testid={`template-card-${id}`}
                className={`overflow-hidden rounded-2xl border bg-card transition-colors ${isSelected ? "border-2 border-brand" : "border-border"}`}>
                <div className="relative h-[280px] overflow-hidden border-b border-border bg-slate-100">
                  <div style={{ width: 560, transform: "scale(0.52)", transformOrigin: "top left" }} className="pointer-events-none">
                    <InvoiceDesignPreview design={expand(id)} garageName={garageName} logoUrl={logoUrl} />
                  </div>
                  {isDefault && <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[10px] font-extrabold text-white"><Star className="h-3 w-3" />DEFAULT</span>}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold uppercase text-midnight">{p.label}</h3>
                    {isSelected && <Check className="h-5 w-5 text-brand" />}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{p.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button data-testid={`select-template-${id}`} onClick={() => { setDesign(expand(id)); setTab("customise"); }}
                      className={`rounded-lg border px-4 py-2 text-xs font-extrabold tracking-wide ${isSelected ? "border-brand bg-brand text-white" : "border-brand bg-background text-brand"}`}>
                      {isSelected ? "Selected" : "Select Template"}
                    </button>
                    <button data-testid={`default-template-${id}`} onClick={() => { const nd = expand(id); setDesign(nd); save(nd); }} disabled={saving}
                      className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-4 py-2 text-xs font-extrabold tracking-wide text-midnight disabled:opacity-50">
                      <Star className="h-3.5 w-3.5" />Set as Default
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "customise" && (
        <div className="grid gap-6 lg:grid-cols-[360px_1fr]" data-testid="customise-panel">
          {/* Controls */}
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-1 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Template</p>
              <p className="text-sm font-bold text-midnight">{TEMPLATE_PRESETS[design.template]?.label}</p>
              <button onClick={() => setTab("template")} data-testid="change-template" className="mt-1 text-xs font-semibold text-brand-2">Change template</button>
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-3 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Logo &amp; branding</p>
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary">
                  {logoUrl ? <img src={logoUrl} alt="logo" className="h-full w-full" style={{ objectFit: design.logo_crop ? "cover" : "contain" }} /> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload"
                    className="inline-flex items-center gap-1.5 rounded-md bg-midnight px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{logoUrl ? "Replace logo" : "Upload logo"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                  <p className="mt-1 text-[11px] text-muted-foreground">PNG or JPG. Used on this invoice and other documents.</p>
                </div>
              </div>
              <label className="mt-3 flex cursor-pointer items-center justify-between text-sm font-semibold text-midnight">
                Show logo on invoice
                <input type="checkbox" data-testid="toggle-show_logo" checked={design.show_logo} onChange={(e) => set({ show_logo: e.target.checked })} className="h-4 w-4 accent-brand" />
              </label>
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
                  <span>Logo size</span><span>{design.logo_size}mm</span>
                </div>
                <input type="range" min="8" max="24" step="1" value={design.logo_size} data-testid="logo-size"
                  onChange={(e) => set({ logo_size: Number(e.target.value) })} className="w-full accent-brand" disabled={!design.show_logo} />
              </div>
              <label className="mt-2 flex cursor-pointer items-center justify-between text-sm font-semibold text-midnight">
                Crop to a neat square
                <input type="checkbox" data-testid="logo-crop" checked={design.logo_crop} onChange={(e) => set({ logo_crop: e.target.checked })} className="h-4 w-4 accent-brand" disabled={!design.show_logo} />
              </label>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Colours & type</p>
              <ColorField label="Primary colour" value={design.primary_color} onChange={(v) => set({ primary_color: v })} testId="color-primary" />
              <ColorField label="Secondary colour" value={design.secondary_color} onChange={(v) => set({ secondary_color: v })} testId="color-secondary" />
              <SelectField label="Font" value={design.font} options={FONTS} onChange={(v) => set({ font: v })} testId="select-font" />
            </div>

            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Layout</p>
              <SelectField label="Header appearance" value={design.header_style} options={HEADERS} onChange={(v) => set({ header_style: v })} testId="select-header" />
              <SelectField label="Spacing" value={design.density} options={DENSITY} onChange={(v) => set({ density: v })} testId="select-density" />
              <SelectField label="Labour & parts" value={design.labour_parts} options={LAYOUT} onChange={(v) => set({ labour_parts: v })} testId="select-layout" />
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-3 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Optional sections</p>
              <div className="space-y-2.5">
                {TOGGLES.filter((t) => t.k !== "show_logo").map((t) => (
                  <label key={t.k} className="flex cursor-pointer items-center justify-between text-sm font-semibold text-midnight">
                    {t.l}
                    <input type="checkbox" data-testid={`toggle-${t.k}`} checked={design[t.k]} onChange={(e) => set({ [t.k]: e.target.checked })} className="h-4 w-4 accent-brand" />
                  </label>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">Invoice number, dates, line items, totals and VAT are always shown as required.</p>
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Footer message</p>
              <textarea data-testid="footer-text" value={design.footer_text} onChange={(e) => set({ footer_text: e.target.value })} rows={2}
                placeholder="e.g. Thank you for choosing us. Registered in England."
                className="w-full rounded-lg border border-border bg-background p-2.5 text-sm text-midnight" />
            </div>

            <div className="flex gap-2">
              <button onClick={() => save()} disabled={saving} data-testid="save-design"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-extrabold tracking-wide text-white disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save design
              </button>
              <button onClick={() => setDesign(expand(design.template))} data-testid="reset-design"
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-4 py-3 text-sm font-bold text-midnight">
                <RotateCcw className="h-4 w-4" />Reset
              </button>
            </div>
          </div>

          {/* Live preview */}
          <div className="lg:sticky lg:top-4 lg:self-start">
            <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Live preview · demo data</p>
            <div className="rounded-2xl border border-border bg-slate-100 p-4" data-testid="live-preview">
              <div className="mx-auto max-w-[620px] shadow-lg">
                <InvoiceDesignPreview design={design} garageName={garageName} logoUrl={logoUrl} />
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "arrange" && (
        <div className="grid gap-6 lg:grid-cols-[360px_1fr]" data-testid="arrange-panel">
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-1 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Arrange sections</p>
              <p className="mb-3 text-xs text-muted-foreground">Drag to reorder the lower sections, or use the arrows. Toggle the eye to show or hide each one.</p>
              <div className="space-y-2" data-testid="arrange-list">
                {design.section_order.map((key, idx) => {
                  const meta = ARRANGE.find((a) => a.k === key);
                  if (!meta) return null;
                  const shown = design[meta.toggle];
                  return (
                    <div key={key} draggable data-testid={`arrange-row-${key}`}
                      onDragStart={() => setDragKey(key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { reorder(dragKey, key); setDragKey(null); }}
                      className={`flex items-center gap-2 rounded-lg border bg-background px-3 py-2.5 ${dragKey === key ? "border-brand opacity-60" : "border-border"}`}>
                      <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />
                      <span className={`flex-1 text-sm font-semibold ${shown ? "text-midnight" : "text-slate-400 line-through"}`}>{meta.l}</span>
                      <button data-testid={`arrange-up-${key}`} onClick={() => move(key, -1)} disabled={idx === 0} className="rounded px-1 text-xs font-bold text-slate-500 disabled:opacity-30">↑</button>
                      <button data-testid={`arrange-down-${key}`} onClick={() => move(key, 1)} disabled={idx === design.section_order.length - 1} className="rounded px-1 text-xs font-bold text-slate-500 disabled:opacity-30">↓</button>
                      <button data-testid={`arrange-toggle-${key}`} onClick={() => set({ [meta.toggle]: !shown })} className="ml-1 text-slate-500 hover:text-brand">
                        {shown ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">Header, customer, vehicle, line items, totals and VAT stay in their required positions so every invoice remains valid.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => save()} disabled={saving} data-testid="save-arrange"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-extrabold tracking-wide text-white disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save layout
              </button>
              <button onClick={() => set({ section_order: [...ALLOWED_ORDER] })} data-testid="reset-arrange"
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-4 py-3 text-sm font-bold text-midnight">
                <RotateCcw className="h-4 w-4" />Reset order
              </button>
            </div>
          </div>
          <div className="lg:sticky lg:top-4 lg:self-start">
            <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Live preview · demo data</p>
            <div className="rounded-2xl border border-border bg-slate-100 p-4" data-testid="arrange-preview">
              <div className="mx-auto max-w-[620px] shadow-lg">
                <InvoiceDesignPreview design={design} garageName={garageName} logoUrl={logoUrl} />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-slate-600">{label}</p>
      <div className="flex items-center gap-2">
        <input type="color" data-testid={`${testId}-swatch`} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-border bg-background" />
        <input type="text" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono text-midnight" />
      </div>
    </div>
  );
}

function SelectField({ label, value, options, onChange, testId }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-slate-600">{label}</p>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold text-midnight">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
